import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { redirectToGoogleLogin, redirectToSalesforceLogin, redirectToHubSpotLogin } from '../api/auth';

function getOAuthErrorMessage(error) {
  switch (error?.trim().toLowerCase()) {
    case 'failed to create user':
      return "We couldn't create your account right now. Please try again later or contact support.";
    case 'invalid or expired state':
      return 'Your sign-in session expired. Please try again.';
    case 'oauth error: access_denied':
      return 'Sign-in was canceled. You can try again whenever you are ready.';
    default:
      return "We couldn't sign you in. Please try again or contact support if the problem continues.";
  }
}

function LoginPage() {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthError] = useState(() => searchParams.get('error'));

  useEffect(() => {
    if (searchParams.has('error')) {
      const cleanParams = new URLSearchParams(searchParams);
      cleanParams.delete('error');
      setSearchParams(cleanParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isAuthenticated && !oauthError) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate, oauthError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-8 text-gray-900">Job Platform</h1>
        <p className="text-center text-gray-600 mb-8">
          Sign in to manage your jobs and workflows
        </p>

        {oauthError && (
          <div role="alert" className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {getOAuthErrorMessage(oauthError)}
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={redirectToGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-lg px-6 py-3 text-gray-700 font-medium hover:bg-gray-50 transition shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>

          <button
            onClick={redirectToSalesforceLogin}
            className="w-full flex items-center justify-center gap-3 bg-[#00A1E0] border border-[#00A1E0] rounded-lg px-6 py-3 text-white font-medium hover:bg-[#0089C1] transition shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10.006 5.415a4.195 4.195 0 0 1 3.045-.875c.943.107 1.788.576 2.347 1.316.467-.168.926-.373 1.373-.612a4.21 4.21 0 0 1-1.844 2.318c.535-.06 1.061-.196 1.565-.405a9.039 9.039 0 0 1-2.1 2.164 5.26 5.26 0 0 1 .01.529c0 5.395-4.105 11.611-11.61 11.611a11.54 11.54 0 0 1-6.266-1.832 8.234 8.234 0 0 0 6.048-1.691 4.088 4.088 0 0 1-3.817-2.835c.248.048.502.071.758.071.368 0 .73-.05 1.071-.139a4.084 4.084 0 0 1-3.275-4.002v-.053c.546.302 1.169.487 1.832.509a4.083 4.083 0 0 1-1.264-5.451 11.586 11.586 0 0 0 8.412 4.267 4.605 4.605 0 0 1-.106-.935 4.084 4.084 0 0 1 7.063-2.792 8.032 8.032 0 0 0 2.592-.991 4.096 4.096 0 0 1-1.795 2.259 8.16 8.16 0 0 0 2.345-.642 8.29 8.29 0 0 1-2.04 2.12z"/>
            </svg>
            Sign in with Salesforce
          </button>

          <button
            onClick={redirectToHubSpotLogin}
            className="w-full flex items-center justify-center gap-3 bg-[#FF5C35] border border-[#FF5C35] rounded-lg px-6 py-3 text-white font-medium hover:bg-[#FF7A59] transition shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.164 7.93V5.084a2.198 2.198 0 0 0-.21-3.35 2.2 2.2 0 0 0-3.352.21.67.67 0 0 0-.075.11l-2.898 4.38-1.638-1.638a1.967 1.967 0 1 0-2.78 2.78l1.638 1.638-4.38 2.898a.67.67 0 0 0-.11.075 2.2 2.2 0 0 0-.21 3.352 2.198 2.198 0 0 0 3.35-.21V18.164l.03.03a2.198 2.198 0 0 0 3.11 0 2.198 2.198 0 0 0 0-3.11l-.03-.03h2.846a.67.67 0 0 0 .11-.075 2.2 2.2 0 0 0 .21-3.352 2.198 2.198 0 0 0-3.35.21l-2.898-4.38 1.638-1.638a1.967 1.967 0 1 0 2.78-2.78z"/>
            </svg>
            Sign in with HubSpot
          </button>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Salesforce or HubSpot users can auto-provision tenants for their account
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
